import type { State } from '@popperjs/core';
import type { Modifier } from '@atlaskit/popper';
import { HEADER_HEIGHT } from '../../constants.tsx';
import type { Column } from '../../types.tsx';
import { calculateStickyColumnsWidth } from '../calculate-sticky-columns-width/index.tsx';

const INLINE_EDIT_POPUP_SCROLL_MARGIN = 8;

const isWithinStickyTableCell = (state: State) => {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const triggerEl = state.elements.reference as Element;
	const closestTd = triggerEl.closest('td');
	// Check if the closest <td> element has an inline z-index style applied.
	// Frozen columns have a z-index, so if found, skip further handling
	// to avoid affecting frozen column inline edit behavior.
	if (closestTd) {
		return globalThis.window.getComputedStyle(closestTd).position === 'sticky';
	}
	return false;
};

export const getInlineEditModifier = ({
	columns,
	getScrollInfo,
	isStickyColumnsVisible,
}: {
	isStickyColumnsVisible: boolean;
	columns: Column[];
	getScrollInfo: () => {
		isScrolling: boolean;
		scrollElement: HTMLElement | null;
		isSmallScreen: boolean;
	};
}): (() => Modifier<'closeOnThreshold', object>[]) => {
	const stickyColumnsWidth = calculateStickyColumnsWidth(columns);
	return () => [
		{
			name: 'closeOnThreshold',
			enabled: true,
			phase: 'write',
			// When inline edit popup is open and the table is being scrolled,
			// hide the popup if it moves outside the table’s visible boundary.
			// No need to check popups from frozen columns.
			fn: ({ state }) => {
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				const isSticky = isWithinStickyTableCell(state as State);
				if (isSticky) return;

				const { scrollElement, isScrolling, isSmallScreen } = getScrollInfo();
				const totalWidth = isStickyColumnsVisible && !isSmallScreen ? stickyColumnsWidth : 0;

				const scrollRect = scrollElement?.getBoundingClientRect();

				if (!scrollRect) {
					return;
				}
				const referenceElementRect = state.elements.reference.getBoundingClientRect();

				const shoudHide =
					referenceElementRect.left < scrollRect.left + totalWidth ||
					referenceElementRect.top < scrollRect.top + HEADER_HEIGHT ||
					referenceElementRect.bottom > scrollRect.bottom;
				if (shoudHide && isScrolling) {
					// eslint-disable-next-line no-param-reassign
					state.elements.popper.style.display = 'none';
				}
			},
			// When the popup is closed and a field is partially hidden (behind merged columns),
			// auto-scroll the table to bring that field back into view.
			// No need to check popups from frozen columns.
			effect: ({ state }) => {
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				const isSticky = isWithinStickyTableCell(state as State);
				if (isSticky) return;
				const { scrollElement, isSmallScreen } = getScrollInfo();
				const totalWidth = isStickyColumnsVisible && !isSmallScreen ? stickyColumnsWidth : 0;

				const scrollRect = scrollElement?.getBoundingClientRect();

				if (!scrollRect) {
					return;
				}
				const referenceElementRect = state.elements.reference.getBoundingClientRect();
				const isPartiallyHidden = referenceElementRect.left < scrollRect.left + totalWidth;

				if (scrollElement && isPartiallyHidden && state.elements.popper.style.display !== 'none') {
					// When clicking a partially hidden field, auto-scrolls it back into the table view
					// and leaves an 8px margin on the left side of the field.
					const scrollAmount =
						scrollRect.left +
						totalWidth -
						referenceElementRect.left +
						INLINE_EDIT_POPUP_SCROLL_MARGIN;

					scrollElement.scrollBy({
						left: -scrollAmount,
						behavior: 'auto',
					});
				}
			},
		},
	];
};
